import {FetchReportResponse, PaginationConfig} from "src/models/reports/FetchReport";

export interface ReportPayload {
    tpTaxReportJSONData: any;
    lastPage: number;
}

/**
 * Backend field names that differ from the UI model property names.
 * The backend returns these keys in the report JSON; we map them to camelCase UI fields.
 */
const BACKEND_FIELDS = {
    PASSTHROUGH_COST: 'PASSTHROUGH_COST',
    PLI_RECONCILIATION: 'PLI_RECONCILIATION',
    PASSTHROUGH_COST_BY_CLI: 'PASSTHROUGH_COST_BY_CLI',
    CLI_PASSTHROUGH_DETAILS: 'CLI_PASSTHROUGH_DETAILS', // Legacy field name
    PLI_RECONCILIATION_BY_CLI: 'PLI_RECONCILIATION_BY_CLI',
};

/**
 * Handles page change and updates the page configuration
 */
export const handlePageChange = (
    pageNumber: number,
    pageSize: number,
    setPageConfig: (config: PaginationConfig) => void
) => {
    setPageConfig({
        pageNumber,
        pageSize
    });
};

/**
 * Resets the report state to initial values
 */
export const resetReportState = (
    setReportData: (data: any) => void,
    setLastPage: (page: number) => void
) => {
    setReportData(null);
    setLastPage(1);
};

/**
 * Parses the report payload from string to JSON
 */
export const parseReportPayload = (payload: string): ReportPayload => {
    return JSON.parse(payload);
};

/**
 * Validates if the report data contains valid headers
 */
export const isValidReport = (reportData: any): boolean => {
    return reportData?.reportHeaders?.length > 0;
};

/**
 * Maps backend UPPER_SNAKE_CASE field names to camelCase UI model properties.
 * Supports both current and legacy field names for backward compatibility.
 */
export const mapBackendFieldsToUIModel = (reportData: any): void => {
    if (reportData[BACKEND_FIELDS.PASSTHROUGH_COST] && !reportData.passthroughCostDetail) {
        reportData.passthroughCostDetail = reportData[BACKEND_FIELDS.PASSTHROUGH_COST];
    }
    if (reportData[BACKEND_FIELDS.PLI_RECONCILIATION] && !reportData.pliReconciliation) {
        reportData.pliReconciliation = reportData[BACKEND_FIELDS.PLI_RECONCILIATION];
    }
    if (reportData[BACKEND_FIELDS.PASSTHROUGH_COST_BY_CLI] && !reportData.cliPassthroughDetails) {
        reportData.cliPassthroughDetails = reportData[BACKEND_FIELDS.PASSTHROUGH_COST_BY_CLI];
    }
    if (reportData[BACKEND_FIELDS.CLI_PASSTHROUGH_DETAILS] && !reportData.cliPassthroughDetails) {
        reportData.cliPassthroughDetails = reportData[BACKEND_FIELDS.CLI_PASSTHROUGH_DETAILS];
    }
    if (reportData[BACKEND_FIELDS.PLI_RECONCILIATION_BY_CLI] && !reportData.cliPliReconciliation) {
        reportData.cliPliReconciliation = reportData[BACKEND_FIELDS.PLI_RECONCILIATION_BY_CLI];
    }
};

/**
 * Handles the report response and updates the state accordingly.
 * Parses the payload, validates report data, maps backend fields, and updates component state.
 */
export const handleFetchReportResponse = (
    fetchReportResponse: FetchReportResponse | null,
    setReportData: (data: any) => void,
    setLastPage: (page: number) => void,
    showErrorMessage: (message: string) => void
) => {
    if (!fetchReportResponse) {
        return;
    }

    if (!fetchReportResponse.payload) {
        resetReportState(setReportData, setLastPage);
        showErrorMessage(fetchReportResponse.statusMessage);
        return;
    }

    try {
        const payload = parseReportPayload(fetchReportResponse.payload);
        const reportData = payload?.tpTaxReportJSONData;

        if (!reportData || !isValidReport(reportData)) {
            resetReportState(setReportData, setLastPage);
            showErrorMessage("Empty report available, nothing to display");
            return;
        }

        mapBackendFieldsToUIModel(reportData);

        setReportData(reportData);
        setLastPage(payload.lastPage);
    } catch (error) {
        resetReportState(setReportData, setLastPage);
        showErrorMessage("Error parsing report data. Contact Engineering Team.");
    }
};
